import React, { useState, useEffect } from "react";
import { Row, Col, Button, Form, Container } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { createAlertMessage, updateAlertMessage } from "../../api/postapi";
import customloader from "../../assets/images/RollingLoader.gif";
import axios from "axios";

const AddAlertMessage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");
  const [linkUrl, setLinkUrl] = useState("");
  const [bgColor, setBgColor] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEdit);

  useEffect(() => {
    if (isEdit) {
      const fetchAlert = async () => {
        try {
          const res = await axios.get(
            process.env.REACT_APP_API_URL + `/alert-messages/${id}`,
            { withCredentials: true }
          );
          const data = res?.data?.data;
          if (data) {
            setMessage(data.message || "");
            setSeverity(data.severity || "info");
            setLinkUrl(data.link_url || "");
            setBgColor(data.bg_color || "");
          }
        } catch (error) {
          toast.error("Failed to fetch alert message", {
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 1500,
            pauseOnHover: false,
            className: "toastMsg",
          });
          navigate("/alert-messages");
        }
        setIsFetching(false);
      };
      fetchAlert();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error("Message is required", {
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 1500,
        pauseOnHover: false,
        className: "toastMsg",
      });
      return;
    }

    setIsLoading(true);
    const payload = {
      message: message.trim(),
      severity,
      link_url: linkUrl.trim() || null,
      bg_color: bgColor.trim() || null,
    };

    try {
      if (isEdit) {
        await updateAlertMessage(id, payload);
        toast.success("Alert message updated successfully", {
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 1500,
          pauseOnHover: false,
          className: "toastMsg",
        });
      } else {
        await createAlertMessage(payload);
        toast.success("Alert message created successfully", {
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 1500,
          pauseOnHover: false,
          className: "toastMsg",
        });
      }
      navigate("/alert-messages");
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || "Something went wrong", {
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 1500,
        pauseOnHover: false,
        className: "toastMsg",
      });
    }
    setIsLoading(false);
  };

  if (isFetching) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "70vh" }}>
        <img src={customloader} alt="customloader" width="100" height="100" />
      </div>
    );
  }

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">{isEdit ? "Edit Alert Message" : "Add Alert Message"}</h4>
        <Button variant="secondary" size="sm" onClick={() => navigate("/alert-messages")}>
          Back
        </Button>
      </div>

      <Form onSubmit={handleSubmit} className="bg-white p-4 rounded-3">
        <Row>
          <Col xs="12" lg="12" className="mb-3">
            <Form.Group>
              <Form.Label className="text-secondary" style={{ fontSize: "14px" }}>
                Message <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter alert message..."
                required
              />
            </Form.Group>
          </Col>

          <Col xs="12" lg="6" className="mb-3">
            <Form.Group>
              <Form.Label className="text-secondary" style={{ fontSize: "14px" }}>
                Severity
              </Form.Label>
              <Form.Select value={severity} onChange={(e) => setSeverity(e.target.value)}>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
              </Form.Select>
            </Form.Group>
          </Col>

          <Col xs="12" lg="6" className="mb-3">
            <Form.Group>
              <Form.Label className="text-secondary" style={{ fontSize: "14px" }}>
                Background Color (optional)
              </Form.Label>
              <div className="d-flex gap-2 align-items-center">
                <Form.Control
                  type="color"
                  value={bgColor || "#ffffff"}
                  onChange={(e) => setBgColor(e.target.value)}
                  style={{ width: "48px", height: "38px", padding: "2px", cursor: "pointer" }}
                  title="Pick a color"
                />
                <Form.Control
                  type="text"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  placeholder="#fff3cd or leave empty"
                  style={{ maxWidth: "200px" }}
                />
                {bgColor && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setBgColor("")}
                    title="Clear color"
                  >
                    Clear
                  </Button>
                )}
              </div>
              <Form.Text className="text-muted" style={{ fontSize: "12px" }}>
                Use the color picker or type a hex code manually. Leave empty for default styling.
              </Form.Text>
            </Form.Group>
          </Col>

          <Col xs="12" lg="12" className="mb-3">
            <Form.Group>
              <Form.Label className="text-secondary" style={{ fontSize: "14px" }}>
                Link URL (optional)
              </Form.Label>
              <Form.Control
                type="text"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com/promo"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col xs="12" className="d-flex gap-2">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : isEdit ? "Update" : "Create"}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/alert-messages")}>
              Cancel
            </Button>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default AddAlertMessage;
